import { Box, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { AlertTriangleIcon } from '@/components/icons/AlertTriangleIcon';
import { ArrowRightIcon } from '@/components/icons/ArrowRightIcon';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { COLORS } from '@/styles/tokens/colors';

import { DialogModal } from './DialogModal/DialogModal';

const MERGE_DIALOG_MAX_WIDTH = 600;

interface MergeRecordsModalProps {
  heading: string;
  subheading: string;
  duplicateName: string;
  destinationInput: ReactNode;
  moved: ReactNode;
  discarded: ReactNode;
  retained: ReactNode;
  retainedHeading: string;
  pending: boolean;
  disabled?: boolean;
  confirmText?: string;
  onClose: () => void;
  onConfirm: () => void;
  children?: ReactNode;
}

export function MergeRecordsModal({
  heading,
  subheading,
  duplicateName,
  destinationInput,
  moved,
  discarded,
  retained,
  retainedHeading,
  pending,
  disabled,
  confirmText = 'Merge and remove duplicate',
  onClose,
  onConfirm,
  children,
}: MergeRecordsModalProps) {
  return (
    <DialogModal
      isOpen
      heading={heading}
      subheading={subheading}
      spacingPxNumber={24}
      dialogPaperSx={{ maxWidth: MERGE_DIALOG_MAX_WIDTH, width: '100%' }}
      dialogActionsSx={{
        borderTop: `1px solid ${COLORS.GRAY[200]}`,
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
      disableBackdropClick
      showCloseButton={!pending}
      onClose={() => {
        if (!pending) onClose();
      }}
      actions={
        <>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <AlertTriangleIcon
              size={14}
              color={COLORS.FUNCTIONAL.WARNING[600]}
            />
            <Typography variant="body-xs" sx={{ color: COLORS.TEXT.SECONDARY }}>
              This can&apos;t be undone.
            </Typography>
          </Stack>
          <Button
            size="sm"
            variant="primary"
            aria-label={confirmText}
            disabled={disabled || pending}
            loading={pending}
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <Stack gap={2}>
        <Box sx={{ border: `1px solid ${COLORS.GRAY[200]}`, borderRadius: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'minmax(0, 1fr) auto minmax(0, 1fr)',
              },
              alignItems: 'center',
              gap: 1.5,
              p: 2,
              bgcolor: COLORS.GRAY[50],
              borderBottom: `1px solid ${COLORS.GRAY[200]}`,
            }}
          >
            <TypeaheadSelectInput
              label="Duplicate"
              disabled
              options={[{ value: duplicateName, display: duplicateName }]}
              value={duplicateName}
              onChange={() => undefined}
            />
            <Box
              sx={{
                color: COLORS.TEXT.SECONDARY,
                pt: { sm: 2.5 },
                transform: { xs: 'rotate(90deg)', sm: 'none' },
                justifySelf: 'center',
              }}
            >
              <ArrowRightIcon size={18} />
            </Box>
            {destinationInput}
          </Box>
          <Stack gap={1.5} p={2}>
            <Typography
              variant="body-xs"
              sx={{
                color: COLORS.TEXT.SECONDARY,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              What happens
            </Typography>
            <Typography variant="body-s" fontWeight={600}>
              Duplicate
            </Typography>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <Box
                flexShrink={0}
                pt={0.25}
                sx={{ color: COLORS.TEXT.SECONDARY }}
              >
                <ArrowRightIcon size={16} />
              </Box>
              <Typography variant="body-s">{moved}</Typography>
            </Stack>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <Box
                flexShrink={0}
                pt={0.25}
                sx={{ color: COLORS.FUNCTIONAL.ERROR[600] }}
              >
                <XCloseIcon size={16} />
              </Box>
              <Typography variant="body-s">{discarded}</Typography>
            </Stack>
            <Typography variant="body-s" fontWeight={600}>
              {retainedHeading}
            </Typography>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <Box
                flexShrink={0}
                pt={0.25}
                sx={{ color: COLORS.FUNCTIONAL.SUCCESS[700] }}
              >
                <CheckIcon size={16} />
              </Box>
              <Typography variant="body-s">{retained}</Typography>
            </Stack>
          </Stack>
        </Box>
        {children}
      </Stack>
    </DialogModal>
  );
}
